import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {QueryResult} from '@apollo/react-common';
import {useMutation, useQuery} from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {
  EXPIRED_MAGIC_LINK_PATH,
  PROGRESS_PATH,
  REQUEST_SIGN_IN_LINK_PATH,
  SELF_ASSIGN_PATH,
} from '../../paths';
import {
  type AccountIdentityQuery,
  type AccountSessionCreateMutation,
  type AccountSessionCreateMutationVariables,
} from '../graphql/types';
import configureUserMonitoring from './configureUserMonitoring';

const MAGIC_LINK_TOKEN_QUERY_PARAM = 't';

export const IdentityQuery = gql`
  query AccountIdentityQuery {
    me {
      driverAccount {
        id
        firstName
        lastName
        phone
        smsOptInStatus
      }
    }
  }
`;

export const SessionCreateMutation = gql`
  mutation AccountSessionCreateMutation($input: DriverAccountSessionCreateInput!) {
    accountSessionCreate: driverAccountSessionCreate(input: $input) {
      userErrors {
        ... on UserError {
          message
        }
      }
    }
  }
`;

type AccountIdentityQueryHookResult = QueryResult<AccountIdentityQuery, unknown>;

type UseIdentityQueryResult = Pick<AccountIdentityQueryHookResult, 'error'> & {
  identity: NonNullable<AccountIdentityQuery['me']>['driverAccount'];
  isLoading: AccountIdentityQueryHookResult['loading'];
};

type UseIdentityQueryOptions = {
  isAuthRequired?: boolean;
};

const useAuth = ({isAuthRequired}: UseIdentityQueryOptions): UseIdentityQueryResult => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [isLoading, setIsLoading] = useState(true);
  const magicLinkTokenRef = useRef(searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM));

  const driverAssignmentMatch = useRouteMatch<{driverAssignmentId: string}>(PROGRESS_PATH);
  const driverAssignmentId = driverAssignmentMatch?.params?.driverAssignmentId;
  const selfAssignDeliveryMatch = useRouteMatch<{deliveryId: string}>(SELF_ASSIGN_PATH);
  const deliveryId = selfAssignDeliveryMatch?.params?.deliveryId;
  const source = searchParams.get('source');
  const isSelfAssign = !!deliveryId && (source === 'qrcode' || source === 'sms');

  const navigateToExpiredMagicLink = useCallback((): void => {
    if (driverAssignmentId) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?driverAssignmentId=${driverAssignmentId}`);
    }

    if (deliveryId || source) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?deliveryId=${deliveryId}&source=${source}`);
    }

    history.push(EXPIRED_MAGIC_LINK_PATH);
  }, [deliveryId, driverAssignmentId, history, source]);

  const navigateToSignIn = useCallback((): void => {
    const redirectParams = isSelfAssign ? `?deliveryId=${deliveryId}&source=${source}` : '';
    history.push(`${REQUEST_SIGN_IN_LINK_PATH}${redirectParams}`);
  }, [isSelfAssign, deliveryId, source, history]);

  const navigateToSelfAssignRegistration = useCallback((): void => {
    const redirectParams = source ? `?source=${source}` : '';
    history.push(`/self_assign_registration/${deliveryId}${redirectParams}`);
  }, [source, deliveryId, history]);

  const [createMagicLinkSession, createMagicLinkSessionQueryResult] = useMutation<
    AccountSessionCreateMutation,
    AccountSessionCreateMutationVariables
  >(SessionCreateMutation, {
    onCompleted: result => {
      if (result.accountSessionCreate?.userErrors?.length) {
        navigateToExpiredMagicLink();
        setIsLoading(false);
      } else {
        meQueryResult.refetch();
      }
    },
    onError: () => {
      navigateToExpiredMagicLink();
      setIsLoading(false);
    },
  });

  const meQueryResult = useQuery<AccountIdentityQuery>(IdentityQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const identity = meQueryResult?.data?.me?.driverAccount;
  // The `Root.me` field returns a GraphQL error as well as the partial data:
  // `{"data":{"me":null}}` when the viewer is logged out. Since the field is
  // being used to determine if the viewer is logged in we need to ignore that
  // error.
  const meQueryError = meQueryResult.error && meQueryResult?.data ? undefined : meQueryResult.error;

  const shouldCreateSession =
    (isAuthRequired || isSelfAssign) &&
    !identity?.id &&
    !meQueryError &&
    !!magicLinkTokenRef.current;
  const shouldNavigateToSelfAssignRegistration =
    isSelfAssign && !!identity?.id && (!identity.firstName || !identity.lastName);
  // Due to the way QR codes are generated in ezrails, drivers can't be sent
  // a link that contains a magic link token. We account for that here by
  // directing them to the page to request a magic link that does contain a
  // token.
  const shouldNavigateToSignIn =
    (isAuthRequired || isSelfAssign || !!meQueryError) && !identity?.id;

  useEffect((): void => {
    if (searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM)) {
      searchParams.delete(MAGIC_LINK_TOKEN_QUERY_PARAM);
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  }, [history, location.pathname, searchParams]);

  useEffect((): void => {
    if (identity) {
      configureUserMonitoring(identity);
    }
  }, [identity]);

  useEffect((): void => {
    if (meQueryResult.loading) {
      return;
    }

    if (shouldNavigateToSelfAssignRegistration) {
      navigateToSelfAssignRegistration();
      setIsLoading(false);
      return;
    }

    if (shouldCreateSession) {
      createMagicLinkSession({variables: {input: {token: `${magicLinkTokenRef.current}`}}});
      return;
    }

    if (shouldNavigateToSignIn) {
      navigateToSignIn();
    }

    setIsLoading(false);
  }, [
    createMagicLinkSession,
    meQueryResult.loading,
    navigateToSelfAssignRegistration,
    navigateToSignIn,
    shouldCreateSession,
    shouldNavigateToSelfAssignRegistration,
    shouldNavigateToSignIn,
  ]);

  return {
    error: meQueryError || createMagicLinkSessionQueryResult.error,
    identity,
    isLoading,
  };
};

export default useAuth;
